import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { formatPhoneNumber, formatDate } from "../../utils";
import { API_BASE_URL } from "../../constants";
import styles from "./UserEditModal.module.css";

/**
 * UserEditModal - Component for displaying and editing user information
 */
const UserEditModal = React.memo(
  ({
    isOpen,
    user,
    userFields,
    onFieldChange,
    isSaving,
    message,
    onSave,
    onCancel,
    onDelete,
  }) => {
    const [copySuccess, setCopySuccess] = useState(false);

    // Generate template message with user's first name
    const getWelcomeMessage = () => {
      const firstName = userFields?.firstName || user?.firstName || "there";
      return `Hey ${firstName}! This is David Hudman from Active Beaches! Were you able to get the link to join the group chat? Just wanted to make sure you were able to get in. I can send it again if you need it 🎾🏐🔥`;
    };

    // Handle copy to clipboard
    const copyToClipboard = () => {
      const message = getWelcomeMessage();
      navigator.clipboard
        .writeText(message)
        .then(() => {
          setCopySuccess(true);
          setTimeout(() => setCopySuccess(false), 2000);
        })
        .catch((err) => {
          console.error("Failed to copy text: ", err);
        });
    };

    // Helper to get the field value or default
    const getFieldValue = (fieldName) => {
      // If the field exists in userFields (being edited), use that
      if (userFields && userFields[fieldName] !== undefined) {
        return userFields[fieldName];
      }

      // Special handling for array fields
      if (["reInterestedInEvents", "selectedEvents"].includes(fieldName)) {
        if (user && user[fieldName]) {
          // If it's already a string, return it
          if (typeof user[fieldName] === "string") {
            return user[fieldName];
          }

          // If it's an array, convert to comma-separated string
          if (Array.isArray(user[fieldName])) {
            return user[fieldName].join(", ");
          }

          // If it's a JSON string, try to parse and convert
          try {
            const parsed = JSON.parse(user[fieldName]);
            if (Array.isArray(parsed)) {
              return parsed.join(", ");
            }
          } catch (e) {
            // Not JSON, return as is
          }
        }
      }

      // Otherwise use the original user data
      return user && user[fieldName] !== undefined ? user[fieldName] : "";
    };

    // If not open, don't render anything
    if (!isOpen || !user) {
      return null;
    }

    return (
      <div className={styles.editModalOverlay}>
        <div className={styles.editModal}>
          <div className={styles.editModalHeader}>
            <h2>Edit User</h2>
            <button
              className={styles.closeButton}
              onClick={onCancel}
              aria-label="Close"
            >
              ×
            </button>
          </div>

          <div className={styles.editModalBody}>
            <div className={styles.userId}>
              <strong>User ID:</strong> {user.userId}
            </div>

            {/* Message Template with Copy Button */}
            <div className={styles.templateMessageContainer}>
              <p className={styles.templateMessageText}>
                {getWelcomeMessage()}
              </p>
              <button
                className={`${styles.copyButton} ${
                  copySuccess ? styles.copySuccess : ""
                }`}
                onClick={copyToClipboard}
              >
                {copySuccess ? "Copied! ✓" : "Copy"}
              </button>
            </div>

            <div className={styles.editForm}>
              {/* Name Fields */}
              <div className={styles.editFormRow}>
                <div className={styles.editFormGroup}>
                  <label htmlFor="firstName">First Name</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={getFieldValue("firstName")}
                    onChange={(e) => onFieldChange("firstName", e.target.value)}
                  />
                </div>

                <div className={styles.editFormGroup}>
                  <label htmlFor="lastName">Last Name</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={getFieldValue("lastName")}
                    onChange={(e) => onFieldChange("lastName", e.target.value)}
                  />
                </div>
              </div>

              {/* Contact Fields */}
              <div className={styles.editFormRow}>
                <div className={styles.editFormGroup}>
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={getFieldValue("email")}
                    onChange={(e) => onFieldChange("email", e.target.value)}
                  />
                </div>

                <div className={styles.editFormGroup}>
                  <label htmlFor="phone">Phone</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={getFieldValue("phone")}
                    onChange={(e) => onFieldChange("phone", e.target.value)}
                  />
                </div>
              </div>

              {/* Sex and GroupMe ID Fields */}
              <div className={styles.editFormRow}>
                <div className={styles.editFormGroup}>
                  <label htmlFor="zsex">Sex</label>
                  <select
                    id="zsex"
                    name="zsex"
                    value={getFieldValue("zsex")}
                    onChange={(e) => onFieldChange("zsex", e.target.value)}
                  >
                    <option value="">Not specified</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                </div>

                <div className={styles.editFormGroup}>
                  <label htmlFor="groupMeUserId">GroupMe User ID</label>
                  <input
                    type="text"
                    id="groupMeUserId"
                    name="groupMeUserId"
                    value={getFieldValue("groupMeUserId")}
                    onChange={(e) =>
                      onFieldChange("groupMeUserId", e.target.value)
                    }
                  />
                </div>
              </div>

              {/* Shirt Size and Referred By Fields */}
              <div className={styles.editFormRow}>
                <div className={styles.editFormGroup}>
                  <label htmlFor="shirtsize">Shirt Size</label>
                  <input
                    type="text"
                    id="shirtsize"
                    name="shirtsize"
                    value={getFieldValue("shirtsize")}
                    onChange={(e) => onFieldChange("shirtsize", e.target.value)}
                  />
                </div>

                <div className={styles.editFormGroup}>
                  <label htmlFor="referredBy">Referred By</label>
                  <input
                    type="text"
                    id="referredBy"
                    name="referredBy"
                    value={getFieldValue("referredBy")}
                    onChange={(e) =>
                      onFieldChange("referredBy", e.target.value)
                    }
                  />
                </div>
              </div>

              {/* Interested In Events */}
              <div className={styles.editFormGroup}>
                <label htmlFor="reInterestedInEvents">
                  Interested In Events (comma-separated)
                </label>
                <textarea
                  id="reInterestedInEvents"
                  name="reInterestedInEvents"
                  rows="3"
                  value={getFieldValue("reInterestedInEvents")}
                  onChange={(e) =>
                    onFieldChange("reInterestedInEvents", e.target.value)
                  }
                  placeholder="e.g. pickleball, volleyball, yoga, soccer"
                ></textarea>
              </div>

              {/* Selected Events */}
              <div className={styles.editFormGroup}>
                <label htmlFor="selectedEvents">
                  Selected Events (comma-separated)
                </label>
                <textarea
                  id="selectedEvents"
                  name="selectedEvents"
                  rows="3"
                  value={getFieldValue("selectedEvents")}
                  onChange={(e) =>
                    onFieldChange("selectedEvents", e.target.value)
                  }
                  placeholder="e.g. 2025-05-19-pickleball"
                ></textarea>
              </div>

              {/* Message */}
              <div className={styles.editFormGroup}>
                <label htmlFor="zmessage">Message</label>
                <textarea
                  id="zmessage"
                  name="zmessage"
                  rows="3"
                  value={getFieldValue("zmessage")}
                  onChange={(e) => onFieldChange("zmessage", e.target.value)}
                ></textarea>
              </div>

              {/* Socials */}
              <div className={styles.editFormGroup}>
                <label htmlFor="socials">Socials</label>
                <input
                  type="text"
                  id="socials"
                  name="socials"
                  value={getFieldValue("socials")}
                  onChange={(e) => onFieldChange("socials", e.target.value)}
                  placeholder="e.g. Instagram, Facebook"
                />
              </div>

              {/* Additional options */}
              <div className={styles.editFormRow}>
                <div className={styles.editFormGroup}>
                  <label htmlFor="zphone2">Secondary Phone</label>
                  <input
                    type="text"
                    id="zphone2"
                    name="zphone2"
                    value={getFieldValue("zphone2")}
                    onChange={(e) => onFieldChange("zphone2", e.target.value)}
                  />
                </div>

                <div className={styles.editFormGroup}>
                  <label htmlFor="dateAdded">Date Added</label>
                  <input
                    type="text"
                    id="dateAdded"
                    name="dateAdded"
                    value={getFieldValue("dateAdded")}
                    onChange={(e) => onFieldChange("dateAdded", e.target.value)}
                    disabled
                  />
                </div>
              </div>

              {message && (
                <div
                  className={`${styles.saveMessage} ${
                    message.includes("success")
                      ? styles.success
                      : message.includes("error")
                      ? styles.error
                      : styles.info
                  }`}
                >
                  {message}
                </div>
              )}
            </div>
          </div>

          <div className={styles.editModalFooter}>
            <button
              className={styles.deleteBtn}
              onClick={() => {
                if (onDelete && user) {
                  // Close the edit modal first, then open delete confirmation
                  onCancel();
                  onDelete(user);
                }
              }}
              disabled={isSaving}
            >
              Delete User
            </button>

            <div className={styles.spacer}></div>

            <button
              className={styles.cancelBtn}
              onClick={onCancel}
              disabled={isSaving}
            >
              Cancel
            </button>

            <button
              className={styles.saveBtn}
              onClick={onSave}
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>
      </div>
    );
  }
);

// PropTypes for component
UserEditModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  user: PropTypes.object,
  userFields: PropTypes.object,
  onFieldChange: PropTypes.func,
  isSaving: PropTypes.bool,
  message: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
};

export default UserEditModal;
